'use client';

import { useEffect } from 'react';
import Bugsnag from "@bugsnag/js";
import MainWrap from "@/components/MainWrap";
import PageHeader from "@/components/PageHeader";
import PageTitle from "@/components/PageTitle";

export default function Error({ error, reset }: { error: Error & { digest?: string }, reset: () => void }) {
  useEffect(() => {
    // When a Server Action ID can't be found it means the client is running JS
    // from an older deployment than the current server (e.g. during a rolling
    // ECS update). Reloading fetches fresh JS so the action IDs re-align.
    // A sessionStorage flag prevents an infinite reload loop in case the error
    // persists even after a fresh page load.
    if (error.message?.includes('Failed to find Server Action')) {
      const reloadKey = 'sa_reload_attempted';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        return;
      }
      // If reload didn't fix it, clear the flag so future navigations can retry,
      // but don't loop — fall through to show the error UI.
      sessionStorage.removeItem(reloadKey);
    }

    // A ChunkLoadError means the browser has HTML from an older deployment that
    // references chunk filenames which no longer exist on the server (the CDN or
    // browser cached the page across a redeploy). Reloading fetches fresh HTML
    // with current chunk references. The same loop-guard pattern as above applies.
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      const reloadKey = 'chunk_reload_attempted';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(reloadKey);
    }

    Bugsnag.notify(error, (event) => {
      event.severity = 'error';
      event.unhandled = true;
    });
  }, [error]);

  return (
    <>
      <PageHeader>
        <PageTitle>
          <h1>Whoops, something went wrong...</h1>
        </PageTitle>
      </PageHeader>
      <MainWrap>
        <div className="main-content">
          <div className="content-main">
            There was a problem loading the page. Please try again later.
          </div>
        </div>
      </MainWrap>
    </>
  );
}